/**
 * GlobalState.stickers 切片（14.16a）：贴纸面板数据的主线程只读镜像。
 *
 * 数据流：面板打开时调 loadStickers（Worker 双层缓存命中零 HTTP，requests
 * 场景 M）→ 回包写切片；后台静默刷新/最近发送置顶的变更走 'chat/stickers'
 * 推送整包替换。发送后 recordStickerSent 只上报 Worker——recent 置顶由推送
 * 回流，不做本地直改（单一数据源）。登出清 null（换账号不残留旧篮子）。
 * UI 接线（面板组件/Composer 入口）归 14.16b。
 */

import type { GlobalStore } from './global';
import type { AppWorkerHost } from './authState';
import type {
  CreatedStickerPack,
  RecentSticker,
  StickerDraftItem,
  StickerMineSnapshot,
  StickerPack,
  StickerPackDraft,
} from '~/worker/contract';

/** null = 未加载（未登录/未打开过面板/拉取失败） */
export type StickersSlice = StickerMineSnapshot | null;

export const STICKERS_INITIAL: StickersSlice = null;

export interface StickersConnection {
  /**
   * 面板打开时调（幂等；Worker 缓存命中零 HTTP）。resolve false=无数据
   * （未登录/无缓存且拉取失败——面板空态提示重试）。
   */
  loadStickers(): Promise<boolean>;
  /** 贴纸发送发帧成功后调（fire-and-forget；recent 置顶走推送回流） */
  recordStickerSent(sent: RecentSticker): void;
  /**
   * 包管理（14.17a；瞬态页面数据不进 GlobalState——loadGroupMembers 同口径）。
   * 读两件失败折 null（页面重试钮）；变更四件失败 reject 带服务端文案（UI
   * toast 原样展示），篮子变化经 'chat/stickers' 推送回流不用调用方改切片。
   */
  loadCreatedPacks(): Promise<readonly CreatedStickerPack[] | null>;
  loadPackDetail(packId: number): Promise<{ pack: CreatedStickerPack; added: boolean } | null>;
  addPack(pack: StickerPack): Promise<boolean>;
  removePack(packId: number): Promise<boolean>;
  deletePack(packId: number): Promise<boolean>;
  uploadSticker(blob: Blob, filename: string): Promise<StickerDraftItem | null>;
  submitPack(draft: StickerPackDraft): Promise<number | null>;
  /** Worker 崩溃重建后重拉（内存 TTL 丢失，kv 暖启动仍在——通常零 HTTP） */
  resync(): void;
  dispose(): void;
}

export function connectStickers(store: GlobalStore, host: AppWorkerHost): StickersConnection {
  let epoch = 0;
  let loadedOnce = false;

  const offPush = host.onPush('chat/stickers', (snapshot) => {
    store.set({ stickers: snapshot });
  });

  const offAuth = store.subscribe(
    (s) => s.auth,
    (auth) => {
      if (auth.status === 'logged-out') {
        epoch += 1; // 在途响应过期
        loadedOnce = false;
        store.set({ stickers: STICKERS_INITIAL });
      }
    },
  );

  function loadStickers(): Promise<boolean> {
    const seq = ++epoch;
    loadedOnce = true;
    return host.call('chat/stickers/load', null).then(
      (snapshot) => {
        if (seq !== epoch) return false;
        if (snapshot) store.set({ stickers: snapshot });
        return snapshot !== null;
      },
      () => false,
    );
  }

  return {
    loadStickers,
    recordStickerSent(sent) {
      void host.call('chat/stickers/recordSent', sent).catch(() => undefined);
    },
    loadCreatedPacks() {
      return host.call('chat/stickers/created', null).catch(() => null);
    },
    loadPackDetail(packId) {
      return host.call('chat/stickers/packDetail', { packId }).catch(() => null);
    },
    addPack(pack) {
      return host.call('chat/stickers/addPack', pack);
    },
    removePack(packId) {
      return host.call('chat/stickers/removePack', { packId });
    },
    deletePack(packId) {
      return host.call('chat/stickers/deletePack', { packId });
    },
    uploadSticker(blob, filename) {
      return host.call('chat/stickers/upload', { blob, filename });
    },
    submitPack(draft) {
      return host.call('chat/stickers/submitPack', draft);
    },
    resync() {
      if (loadedOnce) void loadStickers();
    },
    dispose() {
      offPush();
      offAuth();
    },
  };
}
